import * as stylex from "@stylexjs/stylex";
import { Fragment, useEffect, useRef, useState, type Ref } from "react";
import type { DeltaGap, GridData, ManualDataCell, ManualDataGroup, MetricRow, MeasurementState, TimeBucket, PlatformSection } from "@aco/core";
import { formatManualValue, formatMetricValue } from "@/lib/grid-format";
import type { DeltaDisplay } from "@/lib/search-params";
import "@/design/tokens.stylex";
import { colors, layout, radii, space, text } from "@/design/tokens.stylex";

// The Overview grid (ADR 0005): platform sections, inline Breakdown results and
// Manual data in one dense, hairline-split table. Its rows are read-only
// figures, so they carry no hover fill; a hover is reserved for rows that act.
const NAME_WIDTH = 160;
const BUCKET_WIDTH = 68;
const TOTAL_WIDTH = 88;
const styles = {
  td: {
    kZKoxP: "x1eryijn",
    kZCmMZ: "x1c1uobl",
    kwRFfy: "xs86qnd",
    kVQacm: "xb3r6kr",
    kt9PQ7: "xso031l",
    kfdmCh: "x1q0q8m5",
    kL6WhQ: "xm50ttx",
    kg5iWk: "xlyipyv",
    kXLuUW: "xxymvpz",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  num: {
    kMwMTN: "xgwkh9f",
    k9WMMc: "x1hr2gdg",
    $$css: true
  },
  total: {
    kZCmMZ: "x1mtild6",
    kwRFfy: "xyri2b",
    k2ei4v: "xpilrb4",
    kVhnKS: "x1t7ytsu",
    kGJrpR: "xqpiaqr",
    k63SB2: "x1s688f",
    $$css: true
  },
  stickyName: {
    kVAEAm: "x7wzq59",
    kbCHJM: "xu96u03",
    kY2c9j: "x1vjfegm",
    kWkggS: "x1h1f4i9",
    kMwMTN: "xgwkh9f",
    $$css: true
  },
  sectionEnd: {
    kt9PQ7: "x1qhh985",
    $$css: true
  },
  value: {
    kMwMTN: "xgwkh9f",
    $$css: true
  },
  mutedValue: {
    kMwMTN: "x1i6qc9y",
    $$css: true
  },
  totalValue: {
    k63SB2: "x1s688f",
    $$css: true
  },
  absent: {
    kMwMTN: "xovanlf",
    kkrTdU: "x1146j2",
    $$css: true
  }
};
interface DataGridProps {
  data: GridData;
  // Delta display is URL state (`?deltas=`, default hidden) owned by the
  // reporting route so shared links and reloads keep the reader's choice.
  deltaDisplay: DeltaDisplay;
  // Root element ref used by the PNG export to capture the full grid.
  ref?: Ref<HTMLDivElement>;
  // Manual data (issue 16). When the reader may edit (admin/member, never a
  // Viewer) and a bucket is a single Reporting day, cells accept inline entry.
  // The handlers persist optimistically; they return a promise that rejects on
  // failure so the cell can announce the error and the caller can roll back.
  manualEditable?: boolean;
  onManualSave?: (metricId: string, day: string, value: number) => Promise<void>;
  onManualDelete?: (metricId: string, day: string) => Promise<void>;
}

// Why a cell may understate its real value — computed server-side (see
// buildFreshnessAnnotations in grid-aggregation.ts) so CSV and future
// consumers see the same interpretation instead of each recomputing it from
// platformHealth. `reason` is the human-readable cause.
interface FreshnessFlag {
  reason: string;
}
function measurementNote(state?: MeasurementState) {
  return state === "unmeasured" ? "Not received" : state === "incomplete" ? "Incomplete" : state === "unconfigured" ? "Not configured" : state === "provisional" ? "Provisional" : undefined;
}

// A platform with nothing in the range and nothing to compare against is
// noise: every cell would be a dash or a zero. Total stays, since it names
// what the report covers even when one platform is quiet.
function isBlankSection(section: PlatformSection): boolean {
  if (section.platform === "total") return false;
  return section.rows.every(isBlankRow);
}
type SectionRow = PlatformSection["rows"][number];
function isBlankRow(row: SectionRow): boolean {
  return !row.total && Object.values(row.values).every(value => !value) && row.deltaTotal === undefined && row.absoluteDeltaTotal === undefined;
}

// A Breakdown result with nothing in the range and nothing to compare is a
// heading over a block of dashes. It goes; the other results and the shared
// rows stay, so the section still reads as the channel it is.
function withoutBlankResults(section: PlatformSection): PlatformSection {
  const blankByGroup = new Map<string, boolean>();
  for (const row of section.rows) {
    if (!row.group) continue;
    blankByGroup.set(row.group.id, (blankByGroup.get(row.group.id) ?? true) && isBlankRow(row));
  }
  const blankGroups = new Set([...blankByGroup].filter(([, blank]) => blank).map(([id]) => id));
  if (blankGroups.size === 0) return section;
  return {
    ...section,
    rows: section.rows.filter(row => !row.group || !blankGroups.has(row.group.id))
  };
}
function flagForSection(section: PlatformSection): FreshnessFlag | null {
  return section.understated && section.understatedReason ? {
    reason: section.understatedReason
  } : null;
}
function formatDeltaValue(value: number, display: DeltaDisplay, type: MetricRow["type"], currency: string) {
  const prefix = value >= 0 ? "+" : "";
  return display === "percent" ? `${prefix}${value.toFixed(1)}%` : `${prefix}${formatMetricValue(value, type, currency)}`;
}
function DeltaCell({
  delta,
  absoluteDelta,
  gap,
  display,
  invertColor,
  type,
  currency
}: {
  delta?: number;
  absoluteDelta?: number;
  gap?: DeltaGap;
  display: DeltaDisplay;
  invertColor?: boolean;
  type: MetricRow["type"];
  currency: string;
}) {
  if (display === "hidden") return null;

  // A delta whose own figures weren't measured: a dash, and the server's reason.
  if (gap) return <div className="x17isgxd x1hqrb6m xk50ysn x132q4wb" title={gap.reason} data-export-value="">
        –
      </div>;
  const value = display === "percent" ? delta : absoluteDelta;
  if (value == null || !isFinite(value)) return null;
  const isPositive = value >= 0;
  const isGood = invertColor ? !isPositive : isPositive;
  return <div {...{
    0: {
      className: "x17isgxd x1hqrb6m xk50ysn x132q4wb xnclacp"
    },
    1: {
      className: "x17isgxd x1hqrb6m xk50ysn x132q4wb x1628k4o"
    }
  }[!!isGood << 0]}>
      {formatDeltaValue(value, display, type, currency)}
    </div>;
}
function SectionHeader({
  section,
  flag,
  colSpan,
  firstSection
}: {
  section: PlatformSection;
  flag: FreshnessFlag | null;
  colSpan: number;
  firstSection: boolean;
}) {
  // Neutral section heading — hierarchy comes from spacing and weight, not a
  // colored fill (calm-table direction; trust markers keep their color).
  return <tr>
      <td colSpan={colSpan} {...{
      0: {
        className: "x1c1uobl xs86qnd xb3r6kr x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft xt7dq6l xwcu4w6 x1f9svqt x1qhh985"
      },
      1: {
        className: "x1c1uobl xs86qnd xb3r6kr x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft xt7dq6l x1f9svqt x1qhh985 xh0uz7e"
      }
    }[!!firstSection << 0]}>
        <span className="x7wzq59 xu96u03 xgwkh9f xdj3nnt x1s688f">{section.label}</span>
        {flag ? <span title={flag.reason} data-export-annotation={flag.reason} className="x3nfvp2 x6s0dn4 xsuxsxl x1dyrq8a xnclacp x1hqrb6m xk50ysn">
            <span aria-hidden className="x1v4s8kt xols6we x3kktt9 xtz1k0p" />
            Partial data
          </span> : null}
      </td>
    </tr>;
}
function MetricRowCells({
  row,
  buckets,
  deltaDisplay,
  flag,
  missing,
  currency,
  sectionEnd
}: {
  row: MetricRow;
  buckets: TimeBucket[];
  deltaDisplay: DeltaDisplay;
  flag: FreshnessFlag | null;
  missing: boolean;
  currency: string;
  sectionEnd: boolean;
}) {
  // Mute the numeric value when its source is partial so a low number reads as
  // "incomplete", not "real". The label and deltas stay at full strength.
  const valueStyle = flag ? styles.mutedValue : styles.value;
  const end = sectionEnd && styles.sectionEnd;
  return <tr>
      <td {...stylex.props(styles.td, styles.stickyName, end)}>{row.label}</td>
      {buckets.map(bucket => {
      const value = row.values[bucket.key];
      return <td key={bucket.key} data-export-annotation={measurementNote(row.states?.[bucket.key])} {...stylex.props(styles.td, styles.num, end)}>
            <div {...stylex.props(valueStyle)}>
              {!missing && value != null ? formatMetricValue(value, row.type, currency) : "—"}
            </div>
            {/* Period comparison lives on the Total column only; bucket deltas
                crowd week and month cells. */}
            <DeltaCell delta={row.deltas?.[bucket.key]} absoluteDelta={row.absoluteDeltas?.[bucket.key]} display="hidden" invertColor={row.invertColor} type={row.type} currency={currency} />
          </td>;
    })}
      {/* Total column */}
      <td data-export-annotation={measurementNote(row.measurementState)} {...stylex.props(styles.td, styles.num, styles.total, end)}>
        <div {...stylex.props(styles.totalValue, valueStyle)}>
          {!missing && row.total != null ? formatMetricValue(row.total, row.type, currency) : "—"}
        </div>
        <DeltaCell delta={row.deltaTotal} absoluteDelta={row.absoluteDeltaTotal} gap={row.deltaTotalGap} display={deltaDisplay} invertColor={row.invertColor} type={row.type} currency={currency} />
      </td>
    </tr>;
}

// The column widths and the date row, shared by the grid and its pinned copy.
function GridHead({
  buckets
}: {
  buckets: TimeBucket[];
}) {
  return <>
      <colgroup>
        <col style={{
        width: NAME_WIDTH
      }} />
        {buckets.map(bucket => <col key={bucket.key} style={{
        width: BUCKET_WIDTH
      }} />)}
        <col style={{
        width: TOTAL_WIDTH
      }} />
      </colgroup>
      <thead>
        <tr>
          <th className="x1f38ord x1c1uobl xs86qnd xso031l x1q0q8m5 xaecqrk x7x6yu3 xo1l8bm xdpxx8g xxymvpz xuxw1ft x7wzq59 xu96u03 x1vjfegm x1h1f4i9 xgwkh9f"></th>
          {buckets.map(bucket => <th key={bucket.key} className="x1f38ord x1c1uobl xs86qnd xso031l x1q0q8m5 xaecqrk x1h1f4i9 x7x6yu3 xo1l8bm xxymvpz xuxw1ft xgwkh9f x1hr2gdg">
              {bucket.label}
            </th>)}
          <th className="x1f38ord xso031l x1q0q8m5 xaecqrk x1h1f4i9 x7x6yu3 xxymvpz xuxw1ft xgwkh9f x1hr2gdg x1mtild6 xyri2b xpilrb4 x1t7ytsu xqpiaqr x1s688f">Total</th>
        </tr>
      </thead>
    </>;
}

// Keeps the date row in view while the page scrolls past the grid, without
// giving the grid a scrollbar of its own: a copy of the header rides at the top
// of the page's scroller once the real one has scrolled away, following the
// grid's horizontal scroll. The copy is presentation only — hidden from
// assistive tech and from CSV/PNG export, which read the real header.
function usePinnedHead() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const update = () => {
      const scroller = scrollRef.current;
      const pin = pinRef.current;
      const copy = copyRef.current;
      const head = scroller?.querySelector("thead");
      if (!scroller || !pin || !copy || !head) return;
      const top = pin.getBoundingClientRect().top;
      const headRect = head.getBoundingClientRect();
      const gridBottom = scroller.getBoundingClientRect().bottom;
      copy.hidden = !(headRect.top < top && gridBottom - headRect.height > top);
      copy.scrollLeft = scroller.scrollLeft;
    };
    update();
    // Captured, so the page scroller and the grid's own sideways scroll both count.
    window.addEventListener("scroll", update, {
      capture: true,
      passive: true
    });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, {
        capture: true
      });
      window.removeEventListener("resize", update);
    };
  }, []);
  return {
    scrollRef,
    pinRef,
    copyRef
  };
}
export default function DataGrid({
  data,
  deltaDisplay,
  ref,
  manualEditable = false,
  onManualSave,
  onManualDelete
}: DataGridProps) {
  const {
    scrollRef,
    pinRef,
    copyRef
  } = usePinnedHead();
  const minWidth = `${NAME_WIDTH + data.buckets.length * BUCKET_WIDTH + TOTAL_WIDTH}px`;
  return <div ref={ref} className="x7giv3 x1h1f4i9">
      {data.breakdownsRepeatSpend && <p className="x1ghz6dp xj9id08 x1e3avs9 x7x6yu3" data-export-note>
          Breakdowns repeat some spend. Summary cards remain overall filtered totals.
        </p>}
      <div ref={pinRef} className="x7wzq59 x13vifvy x1355qak xqtp20y" aria-hidden="true" data-export-controls>
        <div ref={copyRef} className="xb3r6kr x1h1f4i9 x1bhlrvt" hidden>
          <table className="xh8yej3 x1vathgz x1gukg7c x140o2bo x1e3avs9 xtu3avg xss6m8b" style={{
          minWidth
        }}>
            <GridHead buckets={data.buckets} />
          </table>
        </div>
      </div>
      <div ref={scrollRef} className="xw2csxc" data-export-scroll data-grid-scroll>
        <table className="xh8yej3 x1vathgz x1gukg7c x140o2bo x1e3avs9 xtu3avg xss6m8b" style={{
        minWidth
      }}>
          <GridHead buckets={data.buckets} />
          <tbody>
            {data.sections.filter(section => !isBlankSection(section)).map(withoutBlankResults).map((section, sectionIndex) => <SectionBlock key={section.platform} section={section} buckets={data.buckets} deltaDisplay={deltaDisplay} flag={flagForSection(section)} missing={data.platformHealth.some(item => item.platform === section.platform && item.missingInRange)} currency={data.currency} firstSection={sectionIndex === 0} />)}
            {data.manualData && data.manualData.rows.length > 0 ? <ManualDataBlock group={data.manualData} deltaDisplay={deltaDisplay} buckets={data.buckets} currency={data.currency} editable={manualEditable} onSave={onManualSave} onDelete={onManualDelete} /> : null}
          </tbody>
        </table>
      </div>
    </div>;
}
const MANUAL_UNIT_LABEL: Record<string, string> = {
  count: "count",
  currency: "currency",
  decimal: "decimal",
  percentage: "%"
};

// Human-readable reason a Manual data cell shows an em dash instead of a
// number. Mirrors the platform measured-state wording so the group reads the
// same as the rest of the grid.
function manualAbsentTitle(state: ManualDataCell["state"]): string {
  return state === "incomplete" ? "Incomplete" : "Not received";
}

// The Manual data group: its own heading immediately below platform Total, one
// row per active Custom metric, and — for editors on a single-day bucket —
// inline entry. It is never summed into any platform section.
function ManualDataBlock({
  group,
  deltaDisplay,
  buckets,
  currency,
  editable,
  onSave,
  onDelete
}: {
  group: ManualDataGroup;
  deltaDisplay: DeltaDisplay;
  buckets: TimeBucket[];
  currency: string;
  editable: boolean;
  onSave?: (metricId: string, day: string, value: number) => Promise<void>;
  onDelete?: (metricId: string, day: string) => Promise<void>;
}) {
  const colSpan = buckets.length + 2;
  return <>
      <tr>
        <td colSpan={colSpan} className="x1c1uobl xs86qnd xb3r6kr x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft xt7dq6l xwcu4w6 x1f9svqt x1qhh985">
          <span className="x7wzq59 xu96u03 xgwkh9f xdj3nnt x1s688f">Manual data</span>
        </td>
      </tr>
      {group.rows.map((row, rowIndex) => {
      const end = rowIndex === group.rows.length - 1;
      return <tr key={row.metricId}>
            <td {...{
          0: {
            className: "x1eryijn x1c1uobl xs86qnd xb3r6kr xso031l x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft x7wzq59 xu96u03 x1vjfegm x1h1f4i9 xgwkh9f"
          },
          1: {
            className: "x1eryijn x1c1uobl xs86qnd xb3r6kr x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft x7wzq59 xu96u03 x1vjfegm x1h1f4i9 xgwkh9f x1qhh985"
          }
        }[!!end << 0]}>
              {row.label}
              <span className="x1dyrq8a x1i6qc9y x1hqrb6m xo1l8bm">
                {MANUAL_UNIT_LABEL[row.unit] ?? row.unit}
              </span>
            </td>
            {buckets.map(bucket => <ManualDataTd key={bucket.key} metricId={row.metricId} metricLabel={row.label} bucketLabel={bucket.label} unit={row.unit} currency={currency} cell={row.cells[bucket.key]} deltaDisplay="hidden" editable={editable} sectionEnd={end} onSave={onSave} onDelete={onDelete} />)}
            <td data-export-value={row.total.value == null ? undefined : String(row.total.value)} {...{
          0: {
            className: "x1eryijn xb3r6kr xso031l x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft xgwkh9f x1hr2gdg x1mtild6 xyri2b xpilrb4 x1t7ytsu xqpiaqr x1s688f"
          },
          2: {
            className: "x1eryijn xb3r6kr xso031l x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft x1hr2gdg x1mtild6 xyri2b xpilrb4 x1t7ytsu xqpiaqr x1s688f xovanlf x1146j2"
          },
          1: {
            className: "x1eryijn xb3r6kr x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft xgwkh9f x1hr2gdg x1mtild6 xyri2b xpilrb4 x1t7ytsu xqpiaqr x1s688f x1qhh985"
          },
          3: {
            className: "x1eryijn xb3r6kr x1q0q8m5 xm50ttx xlyipyv xxymvpz xuxw1ft x1hr2gdg x1mtild6 xyri2b xpilrb4 x1t7ytsu xqpiaqr x1s688f xovanlf x1146j2 x1qhh985"
          }
        }[!!(row.total.value == null) << 1 | !!end << 0]}>
              <div>
                {row.total.value == null ? <span title={manualAbsentTitle(row.total.state)}>—</span> : formatManualValue(row.total.value, row.unit, currency)}
              </div>
              <ManualDelta value={row.total.value} previous={row.total.comparisonValue} display={deltaDisplay} unit={row.unit} currency={currency} />
            </td>
          </tr>;
    })}
    </>;
}
function ManualDelta({
  value,
  previous,
  display,
  unit,
  currency
}: {
  value: number | null;
  previous?: number | null;
  display: DeltaDisplay;
  unit: ManualDataGroup["rows"][number]["unit"];
  currency: string;
}) {
  if (display === "hidden" || value == null || previous == null) return null;
  const change = display === "percent" ? previous === 0 ? undefined : (value - previous) / Math.abs(previous) * 100 : value - previous;
  if (change == null || !Number.isFinite(change)) return null;
  const formatted = display === "percent" ? `${change.toFixed(1)}%` : formatManualValue(change, unit, currency);
  return <div className="x17isgxd x1hqrb6m xk50ysn x132q4wb">
      {change >= 0 ? "+" : ""}
      {formatted}
    </div>;
}

// One Manual data cell. Read-only when the reader cannot edit or the bucket
// spans more than one Reporting day (daily entry only, for now). Editing is an
// ordinary text input: Enter or blur saves, Escape cancels, a blank value
// deletes, and `0` stores an explicit measured zero. Saves are optimistic and
// quiet; a rejected save rolls the input back to its last value and shows an
// accessible inline error.
function ManualDataTd({
  metricId,
  metricLabel,
  bucketLabel,
  unit,
  currency,
  cell,
  deltaDisplay,
  editable,
  sectionEnd,
  onSave,
  onDelete
}: {
  metricId: string;
  metricLabel: string;
  bucketLabel: string;
  unit: ManualDataGroup["rows"][number]["unit"];
  currency: string;
  cell: ManualDataCell | undefined;
  deltaDisplay: DeltaDisplay;
  editable: boolean;
  sectionEnd: boolean;
  onSave?: (metricId: string, day: string, value: number) => Promise<void>;
  onDelete?: (metricId: string, day: string) => Promise<void>;
}) {
  const end = sectionEnd && styles.sectionEnd;
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef(false);
  if (!cell) {
    return <td {...stylex.props(styles.td, styles.num, styles.absent, end)}>
        <div>—</div>
      </td>;
  }
  const original = cell.value == null ? "" : String(cell.value);
  if (!editable || !cell.editable || !cell.day) {
    const absent = cell.value == null;
    return <td data-export-value={cell.value == null ? undefined : String(cell.value)} {...stylex.props(styles.td, styles.num, absent && styles.absent, end)}>
        <div>
          {absent ? <span title={manualAbsentTitle(cell.state)}>—</span> : formatManualValue(cell.value!, unit, currency)}
        </div>
        <ManualDelta value={cell.value} previous={cell.comparisonValue} display={deltaDisplay} unit={unit} currency={currency} />
      </td>;
  }
  const day = cell.day;
  async function commit(raw: string) {
    const trimmed = raw.trim();
    if (trimmed === original) return; // no change — stay quiet
    setError(null);
    // Blank deletes the value (back to "not entered"); a real number saves,
    // including an explicit 0. Save/delete errors are surfaced and rolled back
    // by the route (which owns the optimistic cache), so this cell only guards
    // the one thing it can see locally: a non-numeric entry.
    if (trimmed === "") {
      await onDelete?.(metricId, day);
      return;
    }
    const parsed = Number(trimmed.replace(",", "."));
    if (!Number.isFinite(parsed)) {
      restoreValue();
      setError(`“${trimmed}” is not a number.`);
      return;
    }
    await onSave?.(metricId, day, parsed);
  }
  function restoreValue() {
    if (inputRef.current) inputRef.current.value = original;
  }
  return <td data-export-value={cell.value == null ? "—" : String(cell.value)} data-export-annotation={cell.value == null ? manualAbsentTitle(cell.state) : undefined} {...stylex.props(styles.td, styles.num, cell.value == null && !error && styles.absent, end)}>
      <input ref={inputRef}
    // Remount when the underlying value changes (an optimistic save or a
    // background refetch) so the input reflects the latest stored value.
    key={original} type="text" inputMode="decimal" defaultValue={original} aria-label={`${metricLabel} — ${bucketLabel}`} aria-invalid={error ? true : undefined} {...{
      0: {
        className: "x9f619 xh8yej3 x1fgtraw x1ghz6dp x1wa2ap4 xmkeg23 x1y0btm7 x9r1u3d x1st97zx xzxddps x1wpy2cq xjbqb8w xa2g735 xz2gjom xgwkh9f xjb2p0i x1qlqyl8 xss6m8b x1hr2gdg x1a2a7pz x1gnnqk1 xujb5bh xldbnix xgdialr x1vblb7q"
      },
      1: {
        className: "x9f619 xh8yej3 x1fgtraw x1ghz6dp x1wa2ap4 xmkeg23 x1y0btm7 x9r1u3d x1st97zx xzxddps x1wpy2cq xjbqb8w xa2g735 xz2gjom xjb2p0i x1qlqyl8 xss6m8b x1hr2gdg x1a2a7pz x1gnnqk1 xujb5bh xldbnix xgdialr x1vblb7q xnclacp"
      }
    }[!!error << 0]} onKeyDown={event => {
      if (event.key === "Enter") {
        event.preventDefault();
        event.currentTarget.blur();
      } else if (event.key === "Escape") {
        event.preventDefault();
        cancelRef.current = true;
        restoreValue();
        event.currentTarget.blur();
      }
    }} onBlur={event => {
      if (cancelRef.current) {
        cancelRef.current = false;
        return;
      }
      void commit(event.currentTarget.value);
    }} />
      <ManualDelta value={cell.value} previous={cell.comparisonValue} display={deltaDisplay} unit={unit} currency={currency} />
      {error ? <span role="alert" className="x1lliihq x17bo9yi xnclacp x7x6yu3 xeaf4i8">
          {error}
        </span> : null}
    </td>;
}
function SectionBlock({
  section,
  buckets,
  deltaDisplay,
  flag,
  missing,
  currency,
  firstSection
}: {
  section: PlatformSection;
  buckets: TimeBucket[];
  deltaDisplay: DeltaDisplay;
  flag: FreshnessFlag | null;
  missing: boolean;
  currency: string;
  firstSection: boolean;
}) {
  return <>
      <SectionHeader section={section} flag={flag} colSpan={buckets.length + 2} firstSection={firstSection} />
      {section.rows.map((row, rowIndex) => <Fragment key={row.id}>
          {row.group && section.rows[rowIndex - 1]?.group?.id !== row.group.id && <tr>
              <th scope="rowgroup" colSpan={buckets.length + 2} {...{
          0: {
            className: "x1c1uobl xs86qnd x1q0q8m5 xaecqrk x7x6yu3 xdpxx8g xxymvpz xuxw1ft xt7dq6l xkkwws9 x1f9svqt x1qhh985 xjbqb8w x1e3avs9 xk50ysn"
          },
          1: {
            className: "x1c1uobl xs86qnd x1q0q8m5 xaecqrk x7x6yu3 xdpxx8g xxymvpz xuxw1ft xt7dq6l x1f9svqt x1qhh985 xjbqb8w x1e3avs9 xk50ysn x67pdoy"
          }
        }[!!(rowIndex === 0) << 0]}>
                {row.group.label}
                {row.group.comparisonOnly && <span className="x1o0k9xg x1i6qc9y x1hqrb6m xo1l8bm">
                    Comparison period only; current data absent
                  </span>}
              </th>
            </tr>}
          {!row.group && rowIndex > 0 && section.rows[rowIndex - 1]?.group &&
      // Shared rows follow the results and cover the whole filtered
      // channel, so they are headed Total rather than reading as the
      // tail of the last result.
      <tr>
              <th scope="rowgroup" colSpan={buckets.length + 2} className="x1c1uobl xs86qnd x1q0q8m5 xaecqrk x7x6yu3 xdpxx8g xxymvpz xuxw1ft xt7dq6l xkkwws9 x1f9svqt x1qhh985 xjbqb8w x1e3avs9 xk50ysn">
                Total
              </th>
            </tr>}
          <MetricRowCells row={row} buckets={buckets} deltaDisplay={deltaDisplay} flag={flag} missing={missing} currency={currency} sectionEnd={rowIndex === section.rows.length - 1} />
        </Fragment>)}
    </>;
}